import React from 'react';
import { X } from 'lucide-react';

/**
 * SHARED CHIP TABS
 *
 * Pill-style segmented filter used across the app (e.g. Schedule payment filters).
 * Active pill fills with its accent color; inactive pills are neutral gray.
 *
 * Usage:
 *   <ChipTabs
 *     value={filter}
 *     onChange={setFilter}
 *     testIdPrefix="filter"
 *     tabs={[
 *       { value: 'all', label: 'All' },
 *       { value: 'deposit', label: 'Security Deposit', color: 'amber' },
 *       { value: 'paid', label: 'Paid', count: 4 },
 *     ]}
 *   />
 */

const SIZES = {
  sm: 'px-2.5 py-1 text-xs',
  md: 'px-3 py-1.5 text-xs',
  lg: 'px-4 py-2 text-sm',
};

const ACTIVE_BG = {
  blue: 'bg-blue-600 text-white',
  amber: 'bg-amber-500 text-white',
  green: 'bg-green-600 text-white',
  red: 'bg-red-500 text-white',
  purple: 'bg-purple-600 text-white',
  gray: 'bg-gray-900 text-white',
};

export const ChipTabs = ({
  tabs = [],
  value,
  onChange,
  size = 'md',
  testIdPrefix = 'chip',
  className = '',
}) => {
  return (
    <div className={`flex items-center gap-2 flex-wrap ${className}`} role="tablist">
      {tabs.map((tab) => {
        const isActive = value === tab.value;
        const activeClass = ACTIVE_BG[tab.color] || ACTIVE_BG.blue;
        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(tab.value)}
            data-testid={`${testIdPrefix}-${tab.value}`}
            className={`${SIZES[size] || SIZES.md} font-medium rounded-full shadow-[0_8px_24px_rgba(0,0,0,0.04)] transition-all ${
              isActive ? activeClass : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {tab.count != null ? (
              <span className="inline-flex items-center gap-1.5">
                {tab.label}
                <span className={isActive ? 'opacity-80' : 'text-gray-400'}>{tab.count}</span>
              </span>
            ) : (
              tab.label
            )}
          </button>
        );
      })}
    </div>
  );
};

/**
 * MULTI-SELECT CHIP TABS
 *
 * Like ChipTabs but allows selecting multiple statuses at once (OR filter).
 * `value` is an array of selected values. The "All" chip (allValue) clears the
 * selection back to ['all']. Selecting any specific chip removes 'all';
 * deselecting the last specific chip reverts to ['all'].
 *
 * Usage:
 *   <MultiChipTabs
 *     value={filters}            // e.g. ['paid','deposit']
 *     onChange={setFilters}      // receives the new array
 *     testIdPrefix="filter"
 *     tabs={[{ value:'all', label:'All' }, { value:'paid', label:'Paid' }]}
 *   />
 */
export const MultiChipTabs = ({
  tabs = [],
  value = ['all'],
  onChange,
  size = 'md',
  testIdPrefix = 'chip',
  className = '',
  allValue = 'all',
  removable = false,
}) => {
  const selected = Array.isArray(value) ? value : [value];

  const handleClick = (tabValue) => {
    if (tabValue === allValue) {
      onChange([allValue]);
      return;
    }
    let next = selected.filter((v) => v !== allValue);
    if (next.includes(tabValue)) {
      next = next.filter((v) => v !== tabValue);
    } else {
      next = [...next, tabValue];
    }
    if (next.length === 0) next = [allValue];
    onChange(next);
  };

  return (
    <div className={`flex items-center gap-2 flex-wrap ${className}`} role="tablist">
      {tabs.map((tab) => {
        const isActive = selected.includes(tab.value);
        const activeClass = ACTIVE_BG[tab.color] || ACTIVE_BG.blue;
        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-pressed={isActive}
            onClick={() => handleClick(tab.value)}
            data-testid={`${testIdPrefix}-${tab.value}`}
            className={`${SIZES[size] || SIZES.md} font-medium rounded-full shadow-[0_8px_24px_rgba(0,0,0,0.04)] transition-all ${
              isActive ? activeClass : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <span className="inline-flex items-center gap-1.5">
              {tab.label}
              {tab.count != null && (
                <span className={isActive ? 'opacity-80' : 'text-gray-400'}>{tab.count}</span>
              )}
              {removable && isActive && <X className="h-3 w-3 opacity-90" />}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default ChipTabs;
